import React from 'react'
import {
    Box,
    IconButton,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    useBreakpointValue,
    useDisclosure,
    useStyleConfig
} from '@chakra-ui/react'
import PropTypes from 'prop-types'

import {getAllExpandedFromContentArea} from '../../utils/gdi-content-area'
import {getAggregatedBlockVisibility} from '../../utils/gdi-styles'
import {renderComponentsSwitch} from '../../../utils/contentRenderer'
import GDITextBlock from '../../assets/gdiTextBlock'
import WebDrawer from '../../../../../components/web-drawer'
import {InfoCircle} from '../../../../icons'
import styles from './styles'

/**
 * Shared tooltip component for GDI blocks that author a `TooltipContent`
 * content-area (e.g. GDIHeroSliderBlock, GDIContentTileBlock).
 *
 * Renders an info-circle trigger button. Click behaviour:
 *  - Desktop (>= lg): Chakra Popover anchored to the trigger.
 *  - Mobile (< lg): WebDrawer (hug size) rendered via portal.
 *
 * `TooltipContent` items are expanded GDI blocks (typically GDITextBlock) and
 * are rendered via the standard content renderer switch.
 */
const GDITooltip = ({
    tooltipContent,
    placement = 'right',
    triggerAriaLabel = 'Open tooltip'
}) => {
    const blocks = getAllExpandedFromContentArea(tooltipContent)
    const isMobile = useBreakpointValue({base: true, lg: false})
    const tooltipStyles = useStyleConfig('Tooltip', {variant: 'info'})
    const {isOpen, onClose, onOpen, onToggle} = useDisclosure()

    if (!blocks.length) return null

    /* Hide the trigger on any breakpoint where every authored tooltip block
     * is itself hidden via `HideBlockOn` — otherwise the icon would open an
     * empty popover/drawer on that breakpoint. */
    const visibility = getAggregatedBlockVisibility(blocks)
    if (!visibility.base && !visibility.md && !visibility.lg) return null

    const triggerDisplay = {
        base: visibility.base ? 'inline-flex' : 'none',
        md: visibility.md ? 'inline-flex' : 'none',
        lg: visibility.lg ? 'inline-flex' : 'none'
    }

    const handleTriggerClick = (event) => {
        /* Stop the click from bubbling to ancestors — most notably the
         * GDILinkBlock anchor that may wrap a content tile, which would
         * otherwise navigate away when the tooltip is clicked. */
        event.preventDefault()
        event.stopPropagation()

        if (isMobile) {
            onOpen()
            return
        }
        onToggle()
    }

    const triggerProps = {
        'aria-label': triggerAriaLabel,
        onClick: handleTriggerClick,
        icon: (
            <Box {...styles.tooltipTriggerIcon}>
                <InfoCircle {...styles.infoCircleIcon} />
            </Box>
        ),
        ...styles.tooltipTriggerButton,
        display: triggerDisplay
    }

    const renderContent = () => (
        <>
            {blocks.map((block, idx) => {
                const key = block?.ContentLink?.GuidValue || block?.ContentLink?.Id || idx

                if (block?.__typename === 'GDITextBlock') {
                    return <GDITextBlock key={key} {...block} />
                }

                return <React.Fragment key={key}>{renderComponentsSwitch(block)}</React.Fragment>
            })}
        </>
    )

    return (
        <>
            {isMobile ? (
                <IconButton {...triggerProps} />
            ) : (
                <Popover
                    isLazy
                    isOpen={isOpen}
                    onClose={onClose}
                    variant="small"
                    placement={placement}
                    closeOnBlur={true}
                    gutter={8}
                    strategy="fixed"
                    modifiers={[
                        {
                            name: 'preventOverflow',
                            options: {
                                boundary: 'clippingParents',
                                altAxis: true,
                                padding: 16
                            }
                        },
                        {
                            name: 'flip',
                            options: {
                                padding: 16
                            }
                        }
                    ]}
                >
                    <PopoverTrigger>
                        <IconButton {...triggerProps} />
                    </PopoverTrigger>
                    <PopoverContent {...tooltipStyles} {...styles.popoverContent}>
                        <PopoverArrow bg={tooltipStyles.bg} />
                        <PopoverBody p={tooltipStyles.p}>{renderContent()}</PopoverBody>
                    </PopoverContent>
                </Popover>
            )}

            {isMobile && (
                <WebDrawer
                    isOpen={isOpen}
                    onClose={onClose}
                    size="hug"
                    noBodyStyle={true}
                    headerInContent={false}
                    hideHeaderBorderBottom={true}
                >
                    <Box {...styles.drawerBody}>{renderContent()}</Box>
                </WebDrawer>
            )}
        </>
    )
}

GDITooltip.propTypes = {
    tooltipContent: PropTypes.array,
    placement: PropTypes.string,
    triggerAriaLabel: PropTypes.string
}

export default GDITooltip
